<template>
  <div v-if="entry" class="dispo">
    <div class="dispoCard">
      <div class="who">
        <span class="avatar" :style="{ background: 'var(--hue-amber-soft)' }">{{ initials }}</span>
        <div class="whoText">
          <div class="whoName">{{ entry.contact_name || entry.prospect_e164 }} <span v-if="answered" class="onCall">on call</span></div>
          <div class="whoMeta">{{ meta }}</div>
        </div>
        <span v-if="answered" class="dur">{{ durationLabel }}</span>
      </div>

      <h2 class="prompt">{{ answered ? 'How did the call go?' : 'No one picked up — log it?' }}</h2>
      <div class="grid">
        <button
          v-for="opt in DISPOSITIONS"
          :key="opt.value"
          type="button"
          class="opt"
          :class="{ sel: disposition === opt.value }"
          :style="{ '--tone': opt.tone }"
          :title="opt.desc"
          @click="$emit('update:disposition', opt.value)"
        >{{ opt.label }}</button>
      </div>

      <div class="notesBlock">
        <div class="notesHead">
          <span class="notesLabel"><AppIcon name="pencil" :size="13" />Notes</span>
          <span class="notesHint">Carried over from the call</span>
        </div>
        <textarea
          class="notesInput"
          :value="notes"
          placeholder="Add any final context, objections, next steps…"
          @input="$emit('update:notes', ($event.target as HTMLTextAreaElement).value)"
        ></textarea>
      </div>

      <div class="actions sticky-dock surface-card">
        <p v-if="error" class="err">{{ error }}</p>
        <button class="btn primary" type="button" :disabled="!disposition || submitting" @click="$emit('save')">
          <AppIcon name="checkmark" :size="14" />{{ submitting ? 'Saving…' : 'Save & Next' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import { DISPOSITIONS } from './dialerDispositions'
import type { CallerQueueEntry } from '../../../services/dialerApi'

const props = defineProps<{
  entry: CallerQueueEntry | null
  /** Someone picked up — shows the on-call chip and talk time. */
  answered: boolean
  disposition: string | null
  notes: string
  durationSeconds: number
  error: string | null
  submitting: boolean
}>()
defineEmits<{
  (e: 'update:disposition', value: string): void
  (e: 'update:notes', value: string): void
  (e: 'save'): void
}>()

const initials = computed(() => {
  const n = String(props.entry?.contact_name || '').trim()
  if (!n) return '?'
  const p = n.split(/\s+/).filter(Boolean)
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '?'
})
const meta = computed(() =>
  [props.entry?.contact_title, props.entry?.account_name]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' · '),
)
const durationLabel = computed(() => {
  const s = Math.max(0, props.durationSeconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
})
</script>

<style scoped>
.dispo { flex: 1; min-height: 0; overflow-y: auto; padding: var(--space-5) var(--space-7); display: flex; justify-content: center; }
.dispoCard { width: 100%; max-width: 620px; display: flex; flex-direction: column; gap: var(--space-4); }
.who { display: flex; align-items: center; gap: var(--space-3); }
.avatar { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 999px; font-weight: var(--fw-semibold); color: var(--color-text-primary); flex-shrink: 0; }
.whoText { flex: 1; min-width: 0; }
.whoName { font-size: var(--fs-md); font-weight: var(--fw-semibold); display: flex; align-items: center; gap: var(--space-2); }
.onCall { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 2px 8px; border-radius: 999px; background: var(--color-accent); color: #fff; }
.whoMeta { margin-top: 2px; font-size: var(--fs-sm); color: var(--color-text-secondary); }
.dur { font-size: var(--fs-sm); font-variant-numeric: tabular-nums; color: var(--color-text-secondary); }
.prompt { font-family: var(--font-display); font-size: var(--fs-lg); font-weight: var(--fw-semibold); color: var(--color-text-primary); }
.grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-2); }
.opt { padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); border: 1px solid var(--color-border-subtle); background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit; font-size: var(--fs-sm); font-weight: var(--fw-medium); text-align: left; cursor: pointer; transition: background var(--duration-fast), border-color var(--duration-fast), color var(--duration-fast); }
.opt:hover { background: var(--color-bg-subtle); }
.opt.sel { border-color: var(--tone); background: color-mix(in srgb, var(--tone) 12%, transparent); color: var(--tone); }
.notesBlock { border: 1px solid var(--color-border-subtle); border-radius: var(--radius-lg); background: var(--color-bg-surface); padding: var(--space-4); box-shadow: var(--shadow-sm); }
.notesHead { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: var(--space-2); }
.notesLabel { display: inline-flex; align-items: center; gap: 6px; font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.notesHint { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.notesInput { width: 100%; min-height: 72px; resize: vertical; border: 0; background: transparent; font-family: var(--font-sans); font-size: var(--fs-base); line-height: var(--lh-loose); color: var(--color-text-primary); outline: none; }
.notesInput::placeholder { color: var(--color-text-tertiary); }
.err { flex: 1; min-width: 0; font-size: var(--fs-sm); color: var(--color-danger); }
.dispoCard .actions { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-3); padding: var(--space-3) var(--space-4); border-radius: var(--radius-xl); box-shadow: var(--shadow-lg); }
</style>
